import { MouseEvent, useCallback } from "react";

import {
  EltSyncJobsDocument,
  Notification,
  NotificationType,
  NotificationsDocument,
  useRunJobNowMutation,
} from "@/apollo/types";
import { ButtonProps } from "@/components/elements/Button";
import { confirm } from "@/components/elements/Confirm";
import { useViewDataSourceSlideOver } from "@/components/modules/view-data-source-slideover";
import { useRetryNotification } from "@/features/notifications";
import { LinkProps } from "@tanstack/react-location";

import { useRemoveProtectionNotification } from "./useRemoveProtectionNotification";
import { useRunFullSyncNotification } from "./useRunFullSyncNotification";

export const useNotificationAction = (options?: {
  onCompleted: (message: string) => void;
  onError: (message: string) => void;
}) => {
  const getNotificationActionProps = useNotificationActionHandler(options);
  return {
    getActionProps: getNotificationActionProps,
  };
};

type NotificationActionHandler = Pick<
  LinkProps & ButtonProps,
  "to" | "search" | "hash" | "replace" | "colorScheme"
> & {
  label: string;
  handler?: (e: MouseEvent<any>) => Promise<void>;
};

function useNotificationActionHandler(options?: {
  onCompleted: (message: string) => void;
  onError: (message: string) => void;
}) {
  const { onOpen: viewSync } = useViewDataSourceSlideOver();
  const { retry, loading: retryLoading } = useRetryNotification(options);
  const { removeProtection, loading: removeProtectionLoading } =
    useRemoveProtectionNotification(options);
  const { runFullSync, loading: runFullSyncLoading } =
    useRunFullSyncNotification(options);

  const [runEltJobNow] = useRunJobNowMutation({
    refetchQueries: [EltSyncJobsDocument, NotificationsDocument],
    awaitRefetchQueries: true,
    onCompleted(_data, clientOptions) {
      const sourceStreamName = clientOptions?.variables?.input
        ?.sourceStreamName as string | undefined;
      options?.onCompleted(`${sourceStreamName || "Sync"} will be retried.`);
    },
    onError(error) {
      options?.onError(error.message);
    },
  });

  return useCallback(
    (notification: Notification): NotificationActionHandler[] => {
      const syncId =
        notification.detail.syncId ?? notification.detail.eltSyncId;
      switch (notification.type) {
        case NotificationType.ActivateQuarantine:
          return [
            {
              label: "View sync",
              to: `/reverse-etl/${syncId}`,
            },
          ];
        case NotificationType.ActivateBadQuery:
          return [
            {
              label: "View model",
              to: `/editor/${notification.detail.modelId}`,
            },
          ];
        case NotificationType.AirtableTableNotFound:
          return [
            {
              label: "Go to sync",
              to: `/sources/${syncId}/configuration`,
            },
          ];
        case NotificationType.GoogleSheetsBadNamedRange:
        case NotificationType.Quarantine:
        case NotificationType.PostgresTableInsufficientPrivilege:
        case NotificationType.ChargebeeApiRestricted:
        case NotificationType.DatabaseTableNotFound:
        case NotificationType.CustomConnectorFailedRequest: {
          return [
            {
              label: "View sync",
              handler: async (_e) => {
                viewSync({ syncId: syncId });
              },
              colorScheme: "secondary",
            },
            ...(notification.type === NotificationType.Quarantine
              ? [
                  {
                    label: "Retry sync",
                    handler: async () => {
                      await runEltJobNow({
                        variables: {
                          input: {
                            eltSyncId: notification.detail.eltSyncId,
                            sourceStreamName:
                              notification.detail.sourceStreamName,
                          },
                        },
                      });
                    },
                  },
                ]
              : []),
          ];
        }
        case NotificationType.GoogleInvalidGrant:
        case NotificationType.ConnectorBadOauth:
        case NotificationType.PostgresFailedConnection:
        case NotificationType.PostgresDatabaseInsufficientPrivilege:
        case NotificationType.ConnectorBadApiKey:
        case NotificationType.ApiNotEnoughPermissions:
          return [
            {
              label: "Configure connection",
              to: `/settings/connections/${notification.detail.sourceConnectionId}/configuration`,
            },
          ];
        case NotificationType.OrchestrationWorkflowRunFailed:
          return [
            {
              label: "Go to orchestration",
              to: `/orchestration/${notification.entityId}`,
            },
          ];
        case NotificationType.MaterialisationFailure:
          return [
            {
              label: "Go to model",
              to: `/editor/${notification.detail.modelId}`,
            },
          ];
        case NotificationType.ProtectedStreamFullSync:
          return [
            {
              label: "Remove protection",
              colorScheme: "danger",
              handler: async (e) => {
                const confirmed = await confirm({
                  title: "Warning",
                  type: "danger",
                  message:
                    "Removing protection will run full syncs automatically without notifying you, that may cause data losses. Are you sure you want to proceed?",
                });
                if (!confirmed) {
                  return e.stopPropagation();
                }
                if (!removeProtectionLoading) {
                  const sourceStreamId = notification.detail.sourceStreamId;
                  removeProtection(notification, sourceStreamId);
                }
              },
            },
            {
              label: "Run full sync once",
              colorScheme: "danger",
              handler: async (e) => {
                const confirmed = await confirm({
                  title: "Warning",
                  type: "danger",
                  message:
                    "Running a full sync may cause data loss without a backup. Are you sure you want to proceed?",
                });
                if (!confirmed) {
                  return e.stopPropagation();
                }
                if (!runFullSyncLoading) {
                  const sourceStreamId = notification.detail.sourceStreamId;
                  runFullSync(notification, sourceStreamId);
                }
              },
            },
            {
              label: "Retry sync",
              colorScheme: "primary",
              handler: async (_e) => {
                if (!retryLoading) {
                  retry(notification);
                }
              },
            },
          ];
        default:
          return [];
      }
    },
    [
      viewSync,
      removeProtectionLoading,
      removeProtection,
      runFullSyncLoading,
      runFullSync,
      retryLoading,
      retry,
      runEltJobNow,
    ],
  );
}
